"use client";
import { useState, useEffect, useId, useTransition, useMemo } from "react";
import PropTypes from "prop-types";

import { useSelector, useDispatch } from "react-redux";

import { hydrateFromCriteria } from "@/src/store/filter";
import { urlMaker } from "services/url-maker";

import { setMapOrListingFinalPath } from "src/helpers/map/commons";
import { Ga4WithEventParameters } from "src/helpers/google-tag-manager/events/ga4-with-eventparameters";
import { useRouter, usePathname, useSearchParams } from "next/navigation";

import InnerBox from "../inner-box";
import Button from "components/common/button";
import CityDropdown from "./location-filters/city";
import DistrictsDropdown from "./location-filters/districts";
import TownsDropdown from "./location-filters/towns";
import FreeTextInput from "./freetext";
import MultiSelectFilter from "./params-filter/multi-select";
import AdTypeFilter from "./params-filter/ad-type-filter";
import SingleSelectFilter from "./params-filter/single-select";
import PriceInput from "./params-filter/range-select/price-input";
import MinMaxInput from "./params-filter/range-select/min-max-input";
import MinInput from "./params-filter/range-select/min-input";
import CompaniesDropDown from "./companies-filters";

import styles from "./styles.module.css";

const StaticFilterItems = ({ criteria, screenType, filters }) => {
  const [adTypeInformation, setAdTypeInformation] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isPending, startTransition] = useTransition();
  const id = useId();
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const searchParamsKey = useMemo(() => searchParams.toString(), [searchParams]);
  const criteriaSignature = useMemo(
    () =>
      JSON.stringify({
        pathname,
        search: searchParamsKey,
        criteria,
      }),
    [pathname, searchParamsKey, criteria]
  );

  const dispatch = useDispatch();

  const {
    rangeselectitems: rangeReduxItems,
    multiselectitems: multiReduxItems,
    singleselectitems: singleReduxItems,
    adtypesitems: adTypeReduxItems,
    freetext: freetextRedux,
    city: cityRedux,
    districts: districtRedux,
    towns: townsRedux,
    localities: localitiesRedux,
    companies,
  } = useSelector((state) => state.filter);

  const isProject = criteria?.params?.some((param) => param?.ownerType === "PROJECTS");

  const handleFilterButton = async () => {
    Ga4WithEventParameters({
      eventName: "filtre",
      eventParameters: { label: "click||uygula" },
    });
    setIsSubmitting(true);
    const hasLocations = criteria?.location || cityRedux || districtRedux || townsRedux || localitiesRedux;
    let newCriteria = {
      ...criteria,
      params: [...rangeReduxItems, ...multiReduxItems, ...singleReduxItems, ...adTypeReduxItems],
      freetext: freetextRedux?.value ? freetextRedux : null,
      location: hasLocations
        ? {
            ...criteria?.location,
            city: cityRedux,
            districts: districtRedux,
            towns: townsRedux,
            localities: localitiesRedux,
          }
        : null,
      // reset pagination for all change filter change actions
      pagination: {
        key: "pagination",
        page: 0,
        pageSize: 30,
        horizontalPage: false,
      },
    };

    if (isProject) {
      newCriteria = {
        ...newCriteria,
        company: {
          key: "company_id",
          slug: null,
          value: companies,
        },
      };
    }

    try {
      const res = await urlMaker(newCriteria, "default");
      const finalPath = setMapOrListingFinalPath({ res, screenType });

      if (typeof window !== "undefined") {
        window.location.href = finalPath;
      } else {
        startTransition(() => {
          router.push(finalPath);
        });
      }
    } catch (error) {
      console.error("Filter apply failed", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (!criteria) {
      return;
    }

    dispatch(hydrateFromCriteria(criteria));
  }, [dispatch, criteriaSignature]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    const handlePageShow = (event) => {
      if (event?.persisted) {
        dispatch(hydrateFromCriteria(criteria));
      }
    };

    window.addEventListener("pageshow", handlePageShow);

    return () => {
      window.removeEventListener("pageshow", handlePageShow);
    };
  }, [dispatch, criteria]);

  useEffect(() => {
    if (!filters?.result?.length) {
      setAdTypeInformation([]);
      return;
    }

    const checkboxFilters = filters.result.filter((filter) => ["checkbox", "exists"].includes(filter.type));
    setAdTypeInformation(checkboxFilters);
  }, [filters]);

  return (
    <div className={styles.wrapper}>
      <InnerBox title="Adres">
        <CityDropdown />
        <DistrictsDropdown />
        <TownsDropdown />
      </InnerBox>
      <hr className={styles.lineMargin} />
      <InnerBox title="Arama Kelimesi">
        <FreeTextInput />
      </InnerBox>
      {isProject && (
        <>
          <hr className={styles.lineMargin} />
          <CompaniesDropDown criteria={criteria} />
        </>
      )}
      <hr className={styles.line} />
      {adTypeInformation?.length > 0 && (
        <InnerBox title={isProject ? "Proje Tipi" : "İlan Tipi"}>
          <AdTypeFilter information={adTypeInformation} />
        </InnerBox>
      )}
      <hr className={styles.line} />
      {filters?.result?.map((filter, index) => {
        return (
          <div key={`${id}-${index}`}>
            {filter?.type === "price" && (
              <>
                <InnerBox title={filter?.name}>
                  <PriceInput filter={filter} />
                </InnerBox>
                <hr className={styles.lineMargin} />
              </>
            )}
            {filter?.type === "min_max" && (
              <>
                <InnerBox title={filter?.name}>
                  <MinMaxInput filter={filter} filterKey={filter?.key} />
                </InnerBox>
                <hr className={styles.lineMargin} />
              </>
            )}
            {filter?.type === "min" && (
              <>
                <InnerBox title={filter?.name}>
                  <MinInput filter={filter} filterKey={filter?.key} />
                </InnerBox>
                <hr className={styles.lineMargin} />
              </>
            )}
            {filter?.type === "multi_select" && <MultiSelectFilter filter={filter} />}
            {filter?.type === "single_select" && (
              <>
                <InnerBox isDropdownView isHidden={!filter?.hidden} title={filter?.name}>
                  <SingleSelectFilter filter={filter} />
                </InnerBox>
                <hr className={styles.line} />
              </>
            )}
          </div>
        );
      })}
      <div className={styles.filterButtonWrap}>
        <div className={styles.filterButton}>
          <Button size="M" isLoading={isSubmitting || isPending} onClick={handleFilterButton}>
            Filtrele
          </Button>
        </div>
      </div>
    </div>
  );
};

StaticFilterItems.propTypes = {
  screenType: PropTypes.string,
  criteria: PropTypes.object,
  filters: PropTypes.object,
};

export default StaticFilterItems;
